/**
 * Renewal-reminder delivery view: pick a cohort, see every person with a free
 * trial in flight and whether each of the three billing reminders (12/7/4 days
 * before the first charge) has gone out.
 *
 * Built after Lizzie asked "is there any way to see when/if someone receives
 * those renewal emails, just to spot check they're working" (2026-07-30).
 *
 * Dates arrive from the server as YYYY-MM-DD strings and are rendered verbatim
 * — purchases.deferred_until is a DATE and created_at is a naive timestamp, so
 * parsing them into JS Dates on a browser west of UTC silently shifts every
 * reminder a day earlier. The one decision that must NOT be made by comparing
 * those strings — "has the cron's send window closed" — is computed server-side
 * on the raw timestamp and arrives as `closedNd`.
 */
import { useRef, useState } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { Spinner } from '@components/spinner';
import { Reminder, reminderStatus } from './reminder-status';

type Cohorts = RpxResponse<typeof rpx.emailDelivery.getRenewalCohorts>;
type RenewalData = RpxResponse<typeof rpx.emailDelivery.getRenewalRecipients>;
type Row = RenewalData['recipients'][0];

type Props = {
  cohorts: Cohorts;
  loadError: boolean;
};

// 14 and 2 are the old schedule's reminders; the server only asks for them on
// cohorts whose members actually got them (cohortReminderDays).
const BY_DAYS: Record<number, (r: Row) => Reminder> = {
  14: (r) => ({
    days: 14,
    dueDate: r.due14,
    windowClosed: r.closed14,
    sentDate: r.sent14,
    openedDate: r.opened14,
  }),
  12: (r) => ({
    days: 12,
    dueDate: r.due12,
    windowClosed: r.closed12,
    sentDate: r.sent12,
    openedDate: r.opened12,
  }),
  7: (r) => ({
    days: 7,
    dueDate: r.due7,
    windowClosed: r.closed7,
    sentDate: r.sent7,
    openedDate: r.opened7,
  }),
  4: (r) => ({
    days: 4,
    dueDate: r.due4,
    windowClosed: r.closed4,
    sentDate: r.sent4,
    openedDate: r.opened4,
  }),
  2: (r) => ({
    days: 2,
    dueDate: r.due2,
    windowClosed: r.closed2,
    sentDate: r.sent2,
    openedDate: r.opened2,
  }),
};

/*
 * Cells are derived from the same reminderDays the header renders from, so
 * adding a fifth reminder can't produce a header with no column under it.
 */
function remindersFor(row: Row, reminderDays: number[]): Reminder[] {
  return reminderDays.filter((d) => BY_DAYS[d]).map((d) => BY_DAYS[d](row));
}

function ReminderCell({ reminder, row }: { reminder: Reminder; row: Row }) {
  const status = reminderStatus(reminder, row);
  switch (status.kind) {
    case 'sent':
      return (
        <span class="flex flex-col">
          <span class="text-green-700">Sent {status.date}</span>
          {status.openedDate ? (
            <span class="text-xs text-green-600">opened {status.openedDate}</span>
          ) : (
            <span
              class="text-xs text-gray-400"
              title="We handed this to Mailgun but have had no open event back. Not proof it failed — plenty of people never open."
            >
              no open yet
            </span>
          )}
        </span>
      );
    case 'missed':
      return (
        <span
          class="text-amber-600 font-medium"
          title="The send window for this reminder closed with no record of it going out. This can also show if the membership was cancelled and later reinstated while the window was open."
        >
          Missed ({status.date})
        </span>
      );
    case 'paused':
      return (
        <span
          class="text-gray-500"
          title="This member paused their payments. The sending job skips any reminder due before the pause ends."
        >
          Paused
        </span>
      );
    case 'notApplicable':
      return <span class="text-gray-400">n/a</span>;
    default:
      return <span class="text-gray-500">Due {status.date}</span>;
  }
}

export function RenewalReminders({ cohorts, loadError }: Props) {
  const [cohortId, setCohortId] = useState('');
  const [data, setData] = useState<RenewalData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Guards against a slow response for cohort A landing after B was picked and
  // rendering A's people under B's name.
  const requestRef = useRef(0);

  async function pickCohort(id: string) {
    const token = ++requestRef.current;
    setCohortId(id);
    setData(undefined);
    setSearch('');
    if (!id) {
      return;
    }
    setIsLoading(true);
    try {
      const result = await rpx.emailDelivery.getRenewalRecipients({ courseId: id });
      if (requestRef.current === token) {
        setData(result);
      }
    } catch (err) {
      if (requestRef.current === token) {
        showError(err);
      }
    } finally {
      if (requestRef.current === token) {
        setIsLoading(false);
      }
    }
  }

  const reminderDays = data?.reminderDays || [];
  const term = search.trim().toLowerCase();
  const rows = (data?.recipients || []).filter(
    (r) =>
      !term || r.email.toLowerCase().includes(term) || (r.name || '').toLowerCase().includes(term),
  );
  // Counts describe what's on screen, so they can't contradict the table. The
  // one deliberate difference: "paused" counts members paused right now, while
  // a cell reads "Paused" for a reminder due before a pause that may be over.
  const trialingCount = rows.filter((r) => !r.isCharged).length;
  const chargedCount = rows.length - trialingCount;
  const pausedCount = rows.filter((r) => r.pauseActive).length;
  const missedCount = rows.filter((r) =>
    remindersFor(r, reminderDays).some((rem) => reminderStatus(rem, r).kind === 'missed'),
  ).length;

  if (loadError) {
    return (
      <p class="text-sm text-amber-600">
        Couldn't load the renewal cohorts. The course-email tab above still works — reload the page
        to try again.
      </p>
    );
  }

  return (
    <>
      <p class="text-sm text-gray-500 mb-6 max-w-2xl">
        Free-trial billing reminders go out 12, 7 and 4 days before someone's first charge. Cohorts
        emailed on the old schedule (14, 7, 4 and 2 days, until October 5, 2026) show the reminders
        they actually got. This is the same list the sending job works from, so a cohort showing
        "Due" is one it hasn't reached yet. "Sent" means the job recorded the reminder and handed it
        to Mailgun — it reserves that record just before sending, so an open event underneath is the
        only real confirmation it arrived. Members who paused their payments stay on this list but
        are never emailed. First-charge dates are calculated from our own records, not read back
        from Stripe.
      </p>

      {!cohorts.length ? (
        <p class="text-sm text-gray-400">No free trials are in flight right now.</p>
      ) : (
        <div class="flex flex-wrap gap-4 mb-6">
          <label class="flex flex-col gap-1 text-sm font-medium">
            Cohort
            <select
              class="border border-gray-300 rounded-md px-3 py-2 min-w-96"
              value={cohortId}
              onChange={(e: any) => pickCohort(e.target.value)}
            >
              <option value="">Select a cohort…</option>
              {cohorts.map((c) => (
                <option key={c.courseId} value={c.courseId}>
                  {c.title} — {c.trialingCount ? `${c.trialingCount} on trial` : 'all charged'}
                  {c.trialingCount < c.peopleCount
                    ? `, ${c.peopleCount - c.trialingCount} charged`
                    : ''}
                  , first charge {c.firstChargeFrom}
                  {c.firstChargeTo !== c.firstChargeFrom ? `–${c.firstChargeTo}` : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {isLoading && (
        <div class="flex justify-center py-12">
          <Spinner />
        </div>
      )}

      {data && !isLoading && (
        <>
          <div class="flex flex-wrap items-center gap-4 mb-4">
            <input
              type="search"
              placeholder="Search name or email…"
              class="border border-gray-300 rounded-md px-3 py-2 w-72"
              value={search}
              onInput={(e: any) => setSearch(e.target.value)}
            />
            <span class="text-sm text-gray-500">
              {trialingCount} on trial{chargedCount ? `, ${chargedCount} already charged` : ''}
              {pausedCount ? `, ${pausedCount} paused` : ''}.{' '}
              {missedCount
                ? `${missedCount} have a reminder whose window closed with nothing sent.`
                : 'No reminders are overdue.'}
            </span>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-sm border-t border-gray-200">
              <thead>
                <tr class="text-left text-gray-500">
                  <th class="py-2 pr-4 font-medium">Member</th>
                  <th class="py-2 pr-4 font-medium whitespace-nowrap">First charge</th>
                  {reminderDays.map((d) => (
                    <th key={d} class="py-2 pr-4 font-medium whitespace-nowrap">
                      {d}-day
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.purchaseId} class="border-t border-gray-100">
                    <td class="py-2 pr-4">
                      <div class="flex items-baseline gap-2">
                        <span
                          class="font-medium truncate shrink-0 max-w-[50%]"
                          title={r.name || r.email}
                        >
                          {r.name || '—'}
                        </span>
                        <span class="text-gray-500 truncate" title={r.email}>
                          {r.email}
                        </span>
                      </div>
                    </td>
                    <td class="py-2 pr-4 whitespace-nowrap">
                      {r.firstChargeDate}
                      {r.isCharged && !r.pauseActive && (
                        <span class="text-xs text-gray-400 ml-1">(charged)</span>
                      )}
                      {r.pauseActive && (
                        <span class="text-xs text-gray-400 ml-1">paused until {r.pausedUntil}</span>
                      )}
                    </td>
                    {remindersFor(r, reminderDays).map((reminder) => (
                      <td key={reminder.days} class="py-2 pr-4 whitespace-nowrap align-top">
                        <ReminderCell reminder={reminder} row={r} />
                      </td>
                    ))}
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={2 + reminderDays.length} class="py-8 text-center text-gray-400">
                      {data.recipients.length ? 'No members match.' : 'Nobody in this cohort.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
